'use client';

import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';
import type { Toast } from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';
import type { ToasterProps } from 'react-hot-toast/headless';
import _toast, { useToaster } from 'react-hot-toast/headless';
import { FaRegTimesCircle } from 'react-icons/fa';

import styles from './toast.component.module.scss';

type Props = PropsWithChildren<Omit<ToasterProps, 'children'>>;

export const ToastProvider = ({
  children,
  position = 'bottom-center',
  ...rest
}: Props) => {
  return (
    <>
      {children}
      <Toaster position={position} {...rest} />
    </>
  );
};

export const toast = _toast;

export const useToasts = () => {
  const { toasts } = useToaster();

  return toasts;
};

export const FindyToast = () => {
  const { toasts, handlers } = useToaster();
  const { startPause, endPause, calculateOffset, updateHeight } = handlers;
  const visibleToasts = useMemo(
    () => toasts.filter((t) => t.visible),
    [toasts]
  );
  const checkIsDuplicated = (t: Toast) => {
    const items = visibleToasts.filter((t2) => t2.message === t.message);
    /*
      itemsには、tと同じtoastが必ず含まれるため、
      tがあるメッセージの最初に出てくるtoastである場合は表示したい。
      そのため、items[0].id !== t.idという条件をつけている
    */
    if (items.length > 1 && items[0].id !== t.id) {
      return true;
    }
    return false;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 16,
        left: 0,
        zIndex: 9999,
      }}
      onMouseEnter={startPause}
      onMouseLeave={endPause}
    >
      {visibleToasts.map((t) => {
        if (checkIsDuplicated(t)) {
          return null;
        }
        let bgColor = 'white';
        let textColor = 'inherit';
        switch (t.type) {
          case 'success':
            bgColor = '#f0fdf4';
            textColor = '#15803d';
            break;
          case 'error':
            bgColor = '#fef2f2';
            textColor = '#dc2626';
            break;
          default:
            break;
        }
        const offset = calculateOffset(t, {
          reverseOrder: false,
          gutter: 8,
        });

        const ref = (el: HTMLDivElement) => {
          if (el && typeof t.height !== 'number') {
            const height = el.getBoundingClientRect().height;
            updateHeight(t.id, height);
          }
        };
        return (
          <div
            className={styles.toastContent}
            data-testid="findy-toast-item"
            data-toast-visible={t.visible}
            key={t.id}
          >
            <div
              ref={ref}
              style={{
                width: 'auto',
                minWidth: 240,
                padding: '8px 16px',
                background: bgColor,
                color: textColor,
                transition: 'all 0.5s ease-in-out',
                boxShadow: '10px 10px 10px 0px rgba(0,0,0,0.1)',
                borderRadius: 8,
                transform: `translateY(${offset}px)`,
                zIndex: 9999,
              }}
              {...t.ariaProps}
            >
              <span className={styles.toastText}>
                <span className={styles.toastMessage}>
                  {t.message as string}
                </span>
                <button
                  aria-label="トーストを閉じる"
                  className={styles.toastCloseButton}
                  type="button"
                  onClick={() => toast.remove(t.id)}
                >
                  <FaRegTimesCircle
                    aria-hidden="true"
                    color={textColor}
                    size={18}
                  />
                </button>
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
